import styled from 'styled-components';
import { MediaFrame } from '../../components/Styled/Media';

export const ReelGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 24px;
  padding: 20px 0;
`;

export const LatestReelGrid = styled(ReelGrid)`
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
`;

export const ReelCard = styled.div`
  background: ${({ theme }) => theme.cardBg};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.borderStrong};
  }
`;

export const LatestReelCard = styled(ReelCard)`
  position: relative;
`;

/**
 * Center-screen hover button rendered on LatestReelCard for legacy reels
 * (non-HLS). Clicking it calls `markReelForHlsConvert` to opt the reel
 * into the HLS backfill pipeline. Hidden until the card is hovered so
 * it doesn't clutter the grid, and only rendered on reels where
 * hlsReady is false (see Reels.tsx render logic).
 */
export const ConvertHlsButton = styled.button`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: rgba(124, 58, 237, 0.95);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 7px 12px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s ease, transform 0.15s ease;
  z-index: 2;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  white-space: nowrap;

  ${LatestReelCard}:hover & {
    opacity: 1;
  }

  &:hover {
    transform: translate(-50%, -50%) scale(1.05);
  }

  &:active {
    transform: translate(-50%, -50%) scale(0.96);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const ReelInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 16px;
  background: ${({ theme }) => theme.cardBg};
  color: ${({ theme }) => theme.textColor};
`;

export const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const Tag = styled.span`
  background: ${({ theme }) => theme.colors.primarySoft};
  color: ${({ theme }) => theme.colors.primary};
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
`;

export const VideoBadge = styled.div`
  position: absolute;
  top: 14px;
  right: 54px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

/**
 * Inline pill rendered INSIDE VideoBadge to indicate the reel was
 * transcoded through the HLS pipeline (video-pipeline project) vs a
 * legacy MP4 upload. Stays inside VideoBadge (top-right of thumbnail)
 * so it doesn't collide with the hover avatar on the top-left.
 * Purple background to visually distinguish from the black VideoBadge.
 */
export const HlsPill = styled.span`
  background-color: rgba(124, 58, 237, 0.95);
  color: white;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.5px;
  padding: 3px 8px;
  border-radius: 10px;
  line-height: 1;
`;

export const ExplicitPill = styled.span`
  background-color: rgba(220, 38, 38, 0.95);
  color: white;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.5px;
  padding: 3px 8px;
  border-radius: 10px;
  line-height: 1;
`;

export const CallToActionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-top: 4px;
  padding: 10px 12px;
  background-color: ${({ theme }) => theme.surfaceHover};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 8px;
`;

export const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 0;
`;

export const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 12px;
  padding-top: 24px;
  border-top: 1px solid ${({ theme }) => theme.border};
`;

export const SectionTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 650;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.textColor};
`;

/**
 * Uploader's profile photo on a reel awaiting approval. Small until the
 * card is hovered, then large enough to compare the face with the video.
 */
export const ProfileThumb = styled.img`
  width: 44px;
  height: 44px;
  object-fit: cover;
  border-radius: 10px;
  border: 2px solid rgba(255, 255, 255, 0.9);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
  cursor: pointer;
  transition: width 0.15s ease, height 0.15s ease;

  ${MediaFrame}:hover & {
    width: 112px;
    height: 112px;
  }
`;

export const DescriptionInput = styled.textarea`
  width: 100%;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.inputBg};
  color: ${({ theme }) => theme.textColor};
  font-family: inherit;
  font-size: 14px;
  resize: vertical;
  outline: 0;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primarySoft};
  }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  backdrop-filter: blur(6px);
  background-color: ${({ theme }) => theme.overlayBg};
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

export const ModalContent = styled.div`
  background: ${({ theme }) => theme.cardBg};
  border-radius: 14px;
  width: 100%;
  max-width: 400px;
  max-height: 92vh;
  overflow-y: auto;
  position: relative;
  box-shadow: ${({ theme }) => theme.shadow.lg};
  border: 1px solid ${({ theme }) => theme.borderStrong};
  display: flex;
  flex-direction: column;

  /* Hide scrollbar but keep functionality */
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  width: 32px;
  height: 32px;
  z-index: 1001;

  &:hover {
    background-color: rgba(0, 0, 0, 0.7);
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 20px;
    height: 20px;
    color: white;
  }
`;

export const DeleteButton = styled.button`
  position: absolute;
  top: 12px;
  left: 12px;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(6px);
  border: none;
  color: #ff6b6b;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 600;
  transition: all 0.2s ease;
  z-index: 1001;

  &:hover {
    background-color: rgba(0, 0, 0, 0.7);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const DeleteConfirmationOverlay = styled(ModalOverlay)`
  z-index: 1010;
`;

export const DeleteConfirmationContent = styled.div`
  background: ${({ theme }) => theme.surfaceRaised};
  border: 1px solid ${({ theme }) => theme.borderStrong};
  border-radius: 14px;
  box-shadow: ${({ theme }) => theme.shadow.lg};
  padding: 24px;
  width: 400px;
  max-width: 100%;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const DeleteConfirmationButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 8px;
`;

export const ConfirmButton = styled.button<{ variant: 'danger' | 'cancel' }>`
  padding: 8px 20px;
  border-radius: 8px;
  border: none;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  ${({ variant, theme }) =>
    variant === 'danger'
      ? `
    background-color: ${theme.colors.red};
    color: white;
    &:hover {
      background-color: ${theme.colors.red}DD;
    }
  `
      : `
    background-color: ${theme.colors.gray100};
    color: ${theme.colors.gray800};
    &:hover {
      background-color: ${theme.colors.gray200};
    }
  `}

  &:active {
    transform: scale(0.98);
  }
`;

export const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 9/16;
  background: black;
  overflow: hidden;
`;

export const ReelVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: black;
`;

export const VideoControls = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  display: flex;
  flex-direction: column;
  gap: 12px;
  opacity: 0;
  transition: opacity 0.2s ease;

  ${VideoContainer}:hover & {
    opacity: 1;
  }
`;

export const VideoTimeline = styled.div`
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  cursor: pointer;
  position: relative;

  &:hover {
    height: 6px;
  }
`;

export const VideoProgress = styled.div<{ progress: number }>`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: ${({ progress }) => progress}%;
  background: ${({ theme }) => theme.colors.primary};
  border-radius: 2px;
`;

export const VideoControlsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const PlayPauseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const TimeDisplay = styled.span`
  color: white;
  font-size: 14px;
  font-weight: 500;
`;

export const ThumbnailButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const ThumbnailPreviewContainer = styled.div`
  padding: 16px;
  background: ${({ theme }) => theme.cardBg};
  border-bottom: 1px solid ${({ theme }) => theme.border};
`;

export const ThumbnailPreviewRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const ThumbnailPreviewImage = styled.img`
  width: 80px;
  height: 142px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid ${({ theme }) => theme.colors.primary};
`;

export const ThumbnailActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

export const ThumbnailActionButton = styled.button<{ variant: 'confirm' | 'cancel' }>`
  padding: 8px 16px;
  border-radius: 8px;
  border: none;
  font-family: inherit;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;

  ${({ variant, theme }) =>
    variant === 'confirm'
      ? `
    background-color: ${theme.colors.primary};
    color: white;
    &:hover {
      opacity: 0.9;
    }
  `
      : `
    background-color: ${theme.colors.gray100};
    color: ${theme.colors.gray800};
    &:hover {
      background-color: ${theme.colors.gray200};
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:active {
    transform: scale(0.98);
  }
`;